import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Search,
  Trash2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import {
  useDeletePurpose,
  usePurposesList,
  type PurposeOption,
} from "@/lib/queries/purposes"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Purposes (list)
// LEGACY: apps/admin/src/pages/Purpose.jsx
// API:    POST /admin/list_purpose    (server pagination/sort/search)
//         POST /admin/delete_purpose  (delete)
//
// LIST request body:
//   page, limit, search, sortyBy?, sortDirection?    (sortyBy typo PRESERVED)
//
// LIST columns rendered (same set as legacy):
//   purposekey  → "Image" (img 150px in legacy; rendered slightly smaller here)
//   purpose     → "Name" (sortable)
//   _id (act)   → Update link → /purposes/:id (was inline modal in legacy)
//   _id (act)   → Delete button → POST /admin/delete_purpose {id}
//
// IA NOTE: legacy used an inline modal to update a purpose. v2 uses a
// dedicated /purposes/:id route to match the IA convention from earlier
// batches. The edit form only edits the name (mirroring what the legacy
// update endpoint accepts) — same effective behavior, cleaner navigation.
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

export default function Purposes() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const purposesQ = usePurposesList(params)
  const deleteMut = useDeletePurpose()

  const [deletePurpose, setDeletePurpose] = useState<PurposeOption | null>(null)

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = purposesQ.data?.totalPages ?? 0
  const totalDocs = purposesQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deletePurpose) return
    await deleteMut.mutateAsync(deletePurpose._id)
    setDeletePurpose(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Purposes</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} total`
                : "Tag videos with a purpose"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search purposes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search purposes"
              />
            </div>
            <ExportButton<PurposeOption>
              filenamePrefix="purposes"
              totalEstimate={totalDocs}
              columns={[
                { label: "Name", get: (p) => p.purpose },
                { label: "Image", get: (p) => p.purposekey },
                { label: "ID", get: (p) => p._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/list_purpose", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
            <Button asChild>
              <Link to="/purposes/new">Add purpose</Link>
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {purposesQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load purposes. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Image</TableHead>
                <SortableTH field="purpose" label="Name" {...{ onSortClick, SortIcon }} />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purposesQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={3} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!purposesQ.isLoading && (purposesQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="h-32 text-center text-muted-foreground">
                    No purposes match your filters.
                  </TableCell>
                </TableRow>
              )}
              {purposesQ.data?.docs.map((p) => (
                <TableRow key={p._id}>
                  <TableCell>
                    {p.purposekey ? (
                      <img
                        src={p.purposekey}
                        alt=""
                        loading="lazy"
                        className="h-16 w-16 rounded object-cover"
                      />
                    ) : (
                      <div className="grid h-16 w-16 place-items-center rounded bg-muted text-xs text-muted-foreground">
                        no img
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-medium">
                    {p.purpose || "—"}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    <Button asChild variant="ghost" size="sm" aria-label="Edit purpose">
                      <Link to={`/purposes/${p._id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeletePurpose(p)}
                      aria-label="Delete purpose"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog
        open={!!deletePurpose}
        onOpenChange={(open) => !open && setDeletePurpose(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete purpose?</DialogTitle>
            <DialogDescription>
              {deletePurpose?.purpose || "This purpose"} will be removed.
              Videos referencing it may need to be re-tagged. This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeletePurpose(null)}
              disabled={deleteMut.isPending}
            >
              Keep purpose
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete purpose
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
